/**
 * Identidad del usuario autenticado, derivada de los claims del access token.
 *
 * Opción A del estándar (KEYCLOAK_LOGIN_STANDARD.md §3.2): el token es la única
 * fuente de verdad, no hay tabla de usuarios que consultar ni sesión propia que
 * pedirle al backend. La fila de `USUARIOS` que existe del lado del servidor es
 * sólo el perfil dueño de las conversaciones, y el frontend no la necesita.
 *
 * No hace falta un contexto de React: `initKeycloak()` corre antes de montar la
 * app, así que para cuando cualquier componente renderiza ya hay sesión válida.
 */

import { getClientId, getKeycloak } from "../config/keycloak.config";

/** Rol de cliente que habilita la Biblioteca PQR. Debe existir en `clt-portal-pqr-*`. */
export const ROL_INGRESAR = "pqr.ingresar";

export type UsuarioAutenticado = {
  keycloakId: string;
  username: string;
  email: string | null;
  nombreCompleto: string;
  /** Iniciales para el avatar; "?" si el token no trae nombre ni usuario. */
  iniciales: string;
  realmRoles: string[];
  /** Roles de **este** cliente. Los de otras apps del realm no cuentan aquí. */
  clientRoles: string[];
};

/** Claims que este frontend consume del token. Keycloak envía muchos más. */
type ClaimsToken = {
  sub?: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
};

function calcularIniciales(nombre: string, username: string): string {
  const base = nombre.trim() || username.trim();
  if (!base) return "?";

  const palabras = base.split(/[\s.@_-]+/).filter(Boolean);
  const iniciales = palabras.slice(0, 2).map(p => p[0]);

  return iniciales.join("").toUpperCase() || "?";
}

/**
 * Usuario de la sesión actual.
 *
 * No se cachea a nivel de módulo: `updateToken()` reemplaza `tokenParsed` al
 * renovar, y un valor congelado dejaría roles obsoletos si cambian en Keycloak
 * durante la sesión.
 */
export function getUsuarioActual(): UsuarioAutenticado {
  const claims = (getKeycloak().tokenParsed ?? {}) as ClaimsToken;
  const clientId = getClientId();

  const username = claims.preferred_username ?? "";
  const nombreCompleto =
    claims.name ?? [claims.given_name, claims.family_name].filter(Boolean).join(" ").trim();

  return {
    keycloakId: claims.sub ?? "",
    username,
    // El claim `email` sólo viaja si el cliente pide el scope `email`; un token
    // sin él es perfectamente válido y no debe romper nada.
    email: claims.email ?? null,
    nombreCompleto: nombreCompleto || username,
    iniciales: calcularIniciales(nombreCompleto, username),
    realmRoles: claims.realm_access?.roles ?? [],
    // Sólo los roles de este cliente: `rlm-auth-unificada` aloja varias apps y
    // aplanar todo `resource_access` haría que un rol homónimo de otra de ellas
    // concediera acceso a la Biblioteca PQR.
    clientRoles: (clientId ? claims.resource_access?.[clientId]?.roles : undefined) ?? [],
  };
}

/** `true` si el usuario tiene el rol, sin distinguir si es de realm o de cliente. */
export function tieneRol(rol: string): boolean {
  const { realmRoles, clientRoles } = getUsuarioActual();
  return realmRoles.includes(rol) || clientRoles.includes(rol);
}

/**
 * `true` si el usuario puede usar la aplicación.
 *
 * Es el mismo rol que exige el backend en cada request: chequearlo aquí no
 * agrega seguridad —el token viaja igual y el servidor decide— sino que evita
 * montar una app que respondería 403 a todo, y permite explicar el motivo.
 */
export function puedeIngresar(): boolean {
  return tieneRol(ROL_INGRESAR);
}
